/**
 * サインイン方法（環境ごと）: 戻り先 URL の許可リストと、Google / Discord / Apple / X の OAuth アプリの登録。
 *
 * X 欄（2026-09-27 管理判断 x-default-byo）: 既定は「自分の X アプリの Client ID / Secret を入れる」。
 * サーバーが sharedXApp: true（共用 X アプリが X の承認済み）を返す環境でだけ「設定なしで使う（実費は原価）」の選択肢を出す。
 * WHY サーバーの旗を見るか: 承認前の共用アプリは X の規約上グレーなので、使えない選択肢を画面に出すと「設定なしで動く」と誤解させる。
 *
 * サーバー: apps/server/src/modules/players/oauth/providerConfig.ts
 *   GET  …/environments/:env/auth                 状態（秘密は返らない。末尾のヒントだけ）
 *   PUT  …/environments/:env/auth/settings        許可リスト・有効 / 無効（= config の auth セクションの新しい版）
 *   PUT|DELETE …/environments/:env/auth/providers/:provider   資格情報（サーバーで暗号化して保存）
 *
 * WHY 秘密の入力欄を毎回空で出すか: 保存済みの秘密は API が返さない（画面に出した値は画面共有・拡張機能から漏れる）。
 * 空のまま保存すれば前の秘密を使い続ける = client ID だけ直す時に秘密を入れ直させない。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, envPath, OAUTH_PROVIDERS, type AuthSettings, type OAuthProviderName, type OAuthProviderState } from '../api';
import { CodeBlock, ConfirmDialog, CopyButton, ErrorState, PageHeader, Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { errorText, useT } from '../i18n';
import { useShell } from './ProjectShell';
import { projectEyebrow } from '../projectNav';
import { ACCOUNT_SIGNIN_PROVIDER } from '../productName';

const PROVIDER_NAMES: Record<OAuthProviderName, string> = { google: 'Google', discord: 'Discord', apple: 'Apple', x: 'X' };

export function SignInMethodsPage() {
  const { project, env } = useShell();
  const { t } = useT();
  const path = `${envPath(project.id, env)}/auth`;
  const res = useResource<AuthSettings>(path);
  // 保存の応答（最新の状態）で画面を差し替える。読み直しの往復を待たない
  const [data, setData] = useState<AuthSettings | null>(null);
  useEffect(() => setData(res.data), [res.data]);

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('signin.title')} />
      <p className="muted page-lead">{t('signin.lead')}</p>
      {res.loading && !data && <Spinner />}
      {res.error && <ErrorState error={res.error} what={t('signin.title')} onRetry={res.reload} />}
      {data && (
        <>
          {!data.secretsConfigured && (
            <p className="alert alert-warn" role="status">
              {t('signin.secretsMissing')}
            </p>
          )}
          <RedirectUrlsCard path={path} settings={data} onSaved={setData} />
          {/* KUMODeck でログイン: 鍵の登録なしで on にするだけ。サーバーで使えるときだけ（accountSignIn.available）先頭に出す */}
          {data.accountSignIn?.available && <AccountSignInCard path={path} enabled={data.accountSignIn.enabled} onSaved={setData} />}
          {/* 古いサーバーは X の行を返さないので、返ってきたプロバイダだけ出す */}
          {OAUTH_PROVIDERS.filter((p) => data.providers.some((x) => x.provider === p)).map((p) => (
            <ProviderCard key={`${env}-${p}`} path={path} state={data.providers.find((x) => x.provider === p)!} sharedXApp={!!data.sharedXApp} onSaved={setData} />
          ))}
          <section className="card">
            <h2 className="card-title">{t('signin.sdkTitle')}</h2>
            <CodeBlock
              label={t('common.snippet')}
              code={`// ${t('signin.sdkComment')}\nsignInButton.onclick = () => kumo.auth.signInWithProvider('google');\nlinkButton.onclick = () => kumo.auth.linkProvider('discord');`}
            />
          </section>
        </>
      )}
    </>
  );
}

function RedirectUrlsCard({ path, settings, onSaved }: { path: string; settings: AuthSettings; onSaved: (s: AuthSettings) => void }) {
  const { env } = useShell();
  const { t } = useT();
  const [text, setText] = useState(settings.redirectUrls.join('\n'));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => setText(settings.redirectUrls.join('\n')), [settings.redirectUrls]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const redirectUrls = text.split('\n').map((s) => s.trim()).filter(Boolean);
      const r = await api<AuthSettings>(`${path}/settings`, { method: 'PUT', body: { redirectUrls } });
      onSaved(r);
      setMsg({ ok: true, text: t('signin.saved') });
    } catch (err) {
      const e2 = err as ApiError;
      // config の検証エラーは path（auth.redirectUrls.2）付きで返る。何行目かを添えて見せる
      const issue = e2.issues[0];
      setMsg({ ok: false, text: issue ? `${issue.path}: ${issue.message}` : errorText(e2) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2 className="card-title">{t('signin.redirectTitle')}</h2>
      <p className="small muted">{t('signin.redirectHelp')}</p>
      <form onSubmit={save} className="stack">
        <label className="field">
          <span className="sr-only">{t('signin.redirectTitle')}</span>
          <textarea
            className="textarea mono"
            rows={Math.max(3, text.split('\n').length + 1)}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('signin.redirectPlaceholder')}
            spellCheck={false}
          />
        </label>
        <p className="small muted">{t(`signin.redirectAuto.${env}`)}</p>
        {msg && (
          <p className={`alert ${msg.ok ? 'alert-ok' : 'alert-error'}`} role={msg.ok ? 'status' : 'alert'}>
            {msg.text}
          </p>
        )}
        <div>
          <button className="btn btn-primary" disabled={busy}>
            {busy ? t('common.working') : t('signin.saveUrls')}
          </button>
        </div>
      </form>
    </section>
  );
}

/**
 * KUMODeck でログインの行（2026-10-07 core/kumodeck-signin）。on / off だけ（資格情報・コールバック URL の欄は無い = KUMODeck が登録済み）。
 * 切り替えは他の行と同じ PUT …/auth/settings（config の auth の新しい版）
 */
function AccountSignInCard({ path, enabled, onSaved }: { path: string; enabled: boolean; onSaved: (s: AuthSettings) => void }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const toggle = async (on: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      onSaved(await api<AuthSettings>(`${path}/settings`, { method: 'PUT', body: { providers: { [ACCOUNT_SIGNIN_PROVIDER]: { enabled: on } } } }));
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card provider-card" aria-labelledby="prov-account">
      <div className="card-head">
        <h2 className="card-title" id="prov-account">
          {t('signin.deck.title')}
        </h2>
        <div className="provider-pills">
          <span className={`pill ${enabled ? 'pill-ok' : ''}`}>{enabled ? t('signin.enabled') : t('signin.disabled')}</span>
        </div>
      </div>
      <label className="check">
        <input type="checkbox" checked={enabled} disabled={busy} onChange={(e) => void toggle(e.target.checked)} />
        {t('signin.deck.enable')}
      </label>
      <p className="small muted provider-help">{t('signin.deck.help')}</p>
      {err && (
        <p className="alert alert-error" role="alert">
          {err}
        </p>
      )}
    </section>
  );
}

function ProviderCard({ path, state, sharedXApp, onSaved }: { path: string; state: OAuthProviderState; sharedXApp: boolean; onSaved: (s: AuthSettings) => void }) {
  const { env } = useShell();
  const { t } = useT();
  const name = PROVIDER_NAMES[state.provider];
  const apple = state.provider === 'apple';
  // 自分で登録した資格情報があるか。共用 X アプリで動いている（credentials: 'kumo'）ときは configured でも削除するものが無い
  const own = state.credentials !== undefined ? state.credentials === 'own' : state.configured;
  // X の「設定なしで使う（共用）」/「自分の X アプリ」の選択。共用が使えない環境（既定）では選択肢を出さず常に own
  const offerShared = state.provider === 'x' && sharedXApp;
  const [xMode, setXMode] = useState<'shared' | 'own'>(own ? 'own' : 'shared');
  useEffect(() => setXMode(own ? 'own' : 'shared'), [own]);
  const showForm = !offerShared || xMode === 'own';
  const [clientId, setClientId] = useState(state.clientId ?? '');
  const [secret, setSecret] = useState('');
  const [teamId, setTeamId] = useState(state.teamId ?? '');
  const [keyId, setKeyId] = useState(state.keyId ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    setClientId(state.clientId ?? '');
    setTeamId(state.teamId ?? '');
    setKeyId(state.keyId ?? '');
  }, [state.clientId, state.teamId, state.keyId]);

  const run = async (fn: () => Promise<void>, okText?: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      if (okText) setMsg({ ok: true, text: okText });
    } catch (err) {
      const e = err as ApiError;
      const issue = e.issues[0];
      setMsg({ ok: false, text: issue ? `${issue.path}: ${issue.message}` : errorText(e) });
    } finally {
      setBusy(false);
    }
  };
  const reload = async () => onSaved(await api<AuthSettings>(path));

  const toggle = (enabled: boolean) =>
    run(async () => onSaved(await api<AuthSettings>(`${path}/settings`, { method: 'PUT', body: { providers: { [state.provider]: { enabled } } } })));

  const saveCreds = (e: FormEvent) => {
    e.preventDefault();
    const body = apple
      ? { clientId: clientId.trim(), teamId: teamId.trim(), keyId: keyId.trim(), ...(secret.trim() ? { privateKey: secret.trim() } : {}) }
      : { clientId: clientId.trim(), ...(secret.trim() ? { clientSecret: secret.trim() } : {}) };
    return run(async () => {
      await api(`${path}/providers/${state.provider}`, { method: 'PUT', body });
      setSecret('');
      await reload();
    }, t('signin.saved'));
  };

  const remove = () =>
    run(async () => {
      await api(`${path}/providers/${state.provider}`, { method: 'DELETE' });
      setConfirmRemove(false);
      setSecret('');
      await reload();
    });

  const secretLabel = apple ? t('signin.privateKey') : t('signin.clientSecret');
  const keepHint = own ? (apple ? t('signin.privateKeyKeep') : t('signin.secretKeep', { hint: state.secretHint ?? '…' })) : undefined;

  return (
    <section className="card provider-card" aria-labelledby={`prov-${state.provider}`}>
      <div className="card-head">
        <h2 className="card-title" id={`prov-${state.provider}`}>
          {name}
        </h2>
        <div className="provider-pills">
          <span className={`pill ${state.enabled ? 'pill-ok' : ''}`}>{state.enabled ? t('signin.enabled') : t('signin.disabled')}</span>
          <span className={`pill ${state.configured ? 'pill-accent' : 'pill-warn'}`}>{state.configured ? t('signin.configured') : t('signin.notConfigured')}</span>
        </div>
      </div>

      <label className="check">
        <input type="checkbox" checked={state.enabled} disabled={busy} onChange={(e) => void toggle(e.target.checked)} />
        {t('signin.enable', { provider: name })}
      </label>
      {state.enabled && !state.configured && <p className="small muted">{t('signin.enableNeedsCreds')}</p>}

      {offerShared && (
        <fieldset className="stack">
          <legend className="sr-only">{t('signin.x.choice')}</legend>
          <label className="check">
            <input type="radio" name={`x-mode-${env}`} checked={xMode === 'shared'} disabled={busy} onChange={() => setXMode('shared')} />
            {t('signin.x.shared')}
          </label>
          <label className="check">
            <input type="radio" name={`x-mode-${env}`} checked={xMode === 'own'} disabled={busy} onChange={() => setXMode('own')} />
            {t('signin.x.own')}
          </label>
          {xMode === 'shared' && <p className="small muted">{own ? t('signin.x.sharedSwitch') : t('signin.x.sharedHelp')}</p>}
          {xMode === 'shared' && own && (
            <div className="page-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setConfirmRemove(true)} disabled={busy}>
                {t('signin.remove')}
              </button>
            </div>
          )}
        </fieldset>
      )}

      {showForm && (
      <>
      <p className="small muted provider-help">{t(`signin.help.${state.provider}`)}</p>
      <div className="field">
        <span>{t('signin.callback', { provider: name })}</span>
        <div className="callback-line">
          <code className="mono">{state.callbackUrl}</code>
          <CopyButton text={state.callbackUrl} />
        </div>
      </div>

      <form className="stack" onSubmit={saveCreds}>
        <div className="form-grid">
          <label className="field">
            <span>{apple ? t('signin.servicesId') : t('signin.clientId')}</span>
            <input value={clientId} onChange={(e) => setClientId(e.target.value)} required autoComplete="off" spellCheck={false} />
          </label>
          {apple && (
            <>
              <label className="field">
                <span>{t('signin.teamId')}</span>
                <input value={teamId} onChange={(e) => setTeamId(e.target.value)} required maxLength={10} autoComplete="off" spellCheck={false} />
              </label>
              <label className="field">
                <span>{t('signin.keyId')}</span>
                <input value={keyId} onChange={(e) => setKeyId(e.target.value)} required maxLength={10} autoComplete="off" spellCheck={false} />
              </label>
            </>
          )}
        </div>
        <label className="field">
          <span>{secretLabel}</span>
          {apple ? (
            <textarea
              className="textarea mono"
              rows={4}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={keepHint ?? '-----BEGIN PRIVATE KEY-----'}
              spellCheck={false}
              autoComplete="off"
            />
          ) : (
            // プロバイダの client secret（貼り付けるもの）。autocomplete="new-password" だと Chrome が「強力なパスワードを提案」し保存も勧めた
            // （2026-09-27 creator/auth-forms の報告）。off と各パスワード管理の「無視して」の印で、自動入力・生成・保存の対象から外す
            <input
              type="password"
              name="provider-client-secret"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={keepHint}
              autoComplete="off"
              data-1p-ignore=""
              data-lpignore="true"
              data-bwignore=""
              spellCheck={false}
            />
          )}
          {keepHint && <small className="muted">{keepHint}</small>}
        </label>
        {msg && (
          <p className={`alert ${msg.ok ? 'alert-ok' : 'alert-error'}`} role={msg.ok ? 'status' : 'alert'}>
            {msg.text}
          </p>
        )}
        <div className="page-actions">
          <button className="btn btn-primary" disabled={busy || !clientId.trim()}>
            {busy ? t('common.working') : t('signin.saveCreds')}
          </button>
          {own && (
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmRemove(true)} disabled={busy}>
              {t('signin.remove')}
            </button>
          )}
        </div>
      </form>
      </>
      )}
      {!showForm && msg && (
        <p className={`alert ${msg.ok ? 'alert-ok' : 'alert-error'}`} role={msg.ok ? 'status' : 'alert'}>
          {msg.text}
        </p>
      )}

      <ConfirmDialog
        open={confirmRemove}
        title={t('signin.removeTitle', { provider: name })}
        confirmLabel={t('signin.remove')}
        tone="danger"
        busy={busy}
        onConfirm={() => void remove()}
        onClose={() => setConfirmRemove(false)}
      >
        <p>{offerShared ? t('signin.x.removeBodyShared', { env: t(`env.inline.${env}`) }) : t('signin.removeBody', { provider: name, env: t(`env.inline.${env}`) })}</p>
      </ConfirmDialog>
    </section>
  );
}
